import { useCallback, useEffect, useState } from "react";

/**
 * A custom hook to manage localStorage with React state
 * @template T The type of the stored value
 * @param {string} key The key to use in localStorage
 * @param {T} initialValue The initial value if nothing is stored
 * @returns {[T, (value: T | ((prev: T) => T)) => void, () => void]} Array containing [value, setValue, clearValue]
 */
function useLocalStorage(key, initialValue = null) {
  // Validate key
  if (typeof key !== "string" || key.trim() === "") {
    throw new Error("useLocalStorage: key must be a non-empty string");
  }

  // Initialize state with a function to avoid unnecessary re-renders
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      // If item exists, parse it; otherwise return initialValue
      return item !== null ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Memoized setter function that handles both direct values and functions
  const setValue = useCallback(
    (value) => {
      try {
        // Handle functional updates
        const valueToStore = value instanceof Function ? value(storedValue) : value;

        // Update state
        setStoredValue(valueToStore);

        // Update localStorage
        if (valueToStore === null || valueToStore === undefined) {
          window.localStorage.removeItem(key);
        } else {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
        }
      } catch (error) {
        console.error(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key, storedValue],
  );

  // Clear the stored value
  const clearValue = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
      setStoredValue(initialValue);
    } catch (error) {
      console.error(`Error clearing localStorage key "${key}":`, error);
    }
  }, [key, initialValue]);

  // Sync with localStorage changes from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.storageArea === window.localStorage && event.key === key) {
        try {
          const newValue = event.newValue ? JSON.parse(event.newValue) : initialValue;
          setStoredValue(newValue);
        } catch (error) {
          console.error(`Error parsing storage event for key "${key}":`, error);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [key, initialValue]);

  // Only update localStorage when the value actually changes
  useEffect(() => {
    try {
      const currentValue = window.localStorage.getItem(key);
      const stringifiedNewValue = JSON.stringify(storedValue);

      if (currentValue !== stringifiedNewValue) {
        window.localStorage.setItem(key, stringifiedNewValue);
      }
    } catch (error) {
      console.error(`Error syncing localStorage key "${key}":`, error);
    }
  }, [key, storedValue]);

  return [storedValue, setValue, clearValue];
}

export default useLocalStorage;

// Usage example:
// const [value, setValue, clearValue] = useLocalStorage('myKey', 'initial');
// setValue('new value'); // Set directly
// setValue(prev => prev + ' updated'); // Functional update
// clearValue(); // Reset to initial value and remove from localStorage
